import React from 'react';
import { styles } from './ProductFulfilmentInfo.css';
import { FulfilmentStore, DeliveryOption } from '../../utils/types';
import { DeliveryContainer } from './DeliveryContainer/DeliveryContainer';
import { PickUpInStoreContainer } from './PickupInStoreContainer/PickupInStoreContainer';
import { ProductFulfilmentInfoWrappedProps } from './ProductFulfilmentInfoWrapped';
import { GenericTab } from './ErrorTab/GenericTab';
import { useTracking, trackingContext } from '@jbhi-fi/components/src/Tracking';
import { CUSTOM_EVENT_LOCATION_MODAL_OPENED } from './constants';
import { useLocalisationContext } from '../../context/LocalisationContext';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';

export type ContainerNames = 'delivery' | 'pickup';

export type ProductFulfilmentInfoProps = ProductFulfilmentInfoWrappedProps & {
  isLoading?: boolean;
  isError?: boolean;
  delivery?: {
    yourSuburb?: string;
    options?: Array<DeliveryOption>;
  };
  isDeliveryAvailableMetafield?: boolean;
  isDeliveryOnly?: boolean;
  isPreOrder?: boolean;
  isBackOrder?: boolean;
  isPremiumDeliveryAvailable?: boolean;
  pickup?: {
    yourStore?: FulfilmentStore;
    nextClosestStore?: FulfilmentStore;
    isAvailableOutOfRange?: boolean;
    importantMessaging?: string;
  };
  isInStoreOnly?: boolean;
  isLIA?: boolean;
  isAwaitingManualFetch?: boolean;
  handleFetchClick?: () => void;
  marketplaceSellerName?: string;
};

export const ProductFulfilmentInfo = ({
  isLoading,
  isError,
  delivery,
  isDeliveryAvailableMetafield,
  isDeliveryOnly,
  isPreOrder,
  isPremiumDeliveryAvailable = false,
  pickup,
  setIsAvailabilityWrapperOpen,
  setActiveAvailabilityVariant,
  isInStoreOnly,
  isAwaitingManualFetch,
  isBackOrder,
  handleFetchClick = () => null,
  marketplaceSellerName,
}: ProductFulfilmentInfoProps) => {
  const { tracker } = useTracking({
    context: trackingContext.localisation,
    category: 'localisation',
    group: 'pdp-availability',
  });

  const isNoStoreWithinRange = Boolean(delivery?.yourSuburb && !pickup?.yourStore);
  const { topContainer } = useLocalisationContext();

  const handleClickViewOtherOptions = () => {
    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_LOCATION_MODAL_OPENED));
    tracker.trackClick({ label: 'show-other-delivery-options', source: 'pdp', extraData: { value: true } });
    if (setActiveAvailabilityVariant && setIsAvailabilityWrapperOpen) {
      setActiveAvailabilityVariant('delivery');
      setIsAvailabilityWrapperOpen();
    }
  };
  const handleClickCheckOtherStores = () => {
    window.dispatchEvent(new CustomEvent(CUSTOM_EVENT_LOCATION_MODAL_OPENED));
    tracker.trackClick({ label: 'show-other-instore-options', source: 'pdp', extraData: { value: true } });
    if (setActiveAvailabilityVariant && setIsAvailabilityWrapperOpen) {
      setActiveAvailabilityVariant('inStore');
      setIsAvailabilityWrapperOpen();
    }
  };

  const pickupContainer = (
    <PickUpInStoreContainer
      tabTitle="Your store:"
      isNoStoreWithinRange={isNoStoreWithinRange}
      store={pickup?.yourStore}
      isPreOrder={isPreOrder}
      importantMessaging={pickup?.importantMessaging}
      isDeliveryAvailable={isDeliveryAvailableMetafield}
      nextClosestStore={pickup?.nextClosestStore}
      handleClickCheckOtherStores={handleClickCheckOtherStores}
      isAvailableOutOfRange={pickup?.isAvailableOutOfRange}
      isDeliveryOnly={isDeliveryOnly}
      isError={isError}
    />
  );

  const deliveryContainer = (
    <DeliveryContainer
      yourSuburb={delivery?.yourSuburb}
      yourStore={pickup?.yourStore}
      deliveryOptions={delivery?.options}
      handleClickViewOtherOptions={handleClickViewOtherOptions}
      isInStoreOnly={isInStoreOnly}
      isPremiumDeliveryAvailable={isPremiumDeliveryAvailable}
      marketplaceSellerName={marketplaceSellerName}
      isBackOrder={isBackOrder}
      isError={isError}
    />
  );

  const tabs = [
    { type: 'delivery', content: deliveryContainer },
    { type: 'pickup', content: pickupContainer },
  ];

  if (topContainer !== 'delivery') {
    tabs.reverse();
  }

  return (
    <>
      {isLoading ? (
        <div className={styles.loadingTab} data-testid="fulfillment-tab-spinner">
          <Spinner />
        </div>
      ) : (
        tabs.map(({ type, content }) => (
          <GenericTab
            key={type}
            isFetch={isAwaitingManualFetch}
            handleFetchClick={handleFetchClick}
            isLoading={isLoading}
            isError={isError}
            isDelivery={type === 'delivery'}
          >
            {content}
          </GenericTab>
        ))
      )}
    </>
  );
};
